import React, { useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Collapse,
  styled
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Add as AddIcon,
  Work as ContractorIcon
} from '@mui/icons-material';

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    color: '#fff',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.23)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#00BCD4',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
    '&.Mui-focused': {
      color: '#00BCD4',
    },
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  padding: theme.spacing(3),
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(3),
}));

const ContractorPaymentForm = ({ onSubmit, isSubmitting }) => {
  const [formData, setFormData] = useState({
    contractorName: '',
    amount: '',
    paymentDate: '',
    paymentMethod: '',
    recurringPayment: false,
    paymentFrequency: '',
    isContractor: false,
    w9Details: {
      name: '',
      businessName: '',
      taxClassification: '',
      address: '',
      tin: '',
    },
    contractDetails: {
      compensation: '',
      startDate: '',
      endDate: '',
      scopeOfWork: '',
    },
  });

  const [error, setError] = useState('');
  const [documents, setDocuments] = useState([]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleW9Change = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      w9Details: {
        ...prev.w9Details,
        [name]: value
      }
    }));
  };

  const handleContractChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      contractDetails: {
        ...prev.contractDetails,
        [name]: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      await onSubmit(formData);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Collapse in={!!error}>
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      </Collapse>

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12}>
          <StyledPaper>
            <Typography variant="h6" sx={{ mb: 3, color: '#00BCD4' }}>
              Basic Information
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label="Contractor/Vendor Name"
                  name="contractorName"
                  value={formData.contractorName}
                  onChange={handleChange}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label="Amount"
                  name="amount"
                  type="number"
                  value={formData.amount}
                  onChange={handleChange}
                  required
                  InputProps={{
                    startAdornment: '$'
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  type="date"
                  label="Payment Date"
                  name="paymentDate"
                  value={formData.paymentDate}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Payment Method
                  </InputLabel>
                  <Select
                    name="paymentMethod"
                    value={formData.paymentMethod}
                    onChange={handleChange}
                    required
                    sx={{ color: '#fff' }}
                  >
                    <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                    <MenuItem value="Check">Check</MenuItem>
                    <MenuItem value="ACH">ACH</MenuItem>
                    <MenuItem value="Wire">Wire</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </StyledPaper>
        </Grid>

        {/* Recurring Payment Toggle */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch 
                checked={formData.recurringPayment}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  recurringPayment: e.target.checked
                }))}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#00BCD4',
                  },
                }}
              />
            }
            label="Recurring Payment"
          />
        </Grid>

        {/* W9 Information */}
        {formData.isContractor && (
          <Grid item xs={12}>
            <StyledPaper>
              <Typography variant="h6" sx={{ mb: 3, color: '#00BCD4' }}>
                W9 Information
              </Typography>
              
              <Grid container spacing={2}>
                {/* W9 fields */}
              </Grid>
            </StyledPaper>
          </Grid>
        )}

        {/* Submit Button */}
        <Grid item xs={12}>
          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              backgroundColor: '#00BCD4',
              '&:hover': {
                backgroundColor: '#00ACC1',
              },
              '&:disabled': {
                backgroundColor: 'rgba(0, 188, 212, 0.5)',
              }
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Payment Request'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ContractorPaymentForm; 